import { useMemo, useState } from 'react';
import type {
  RunSession,
  SkiTickData,
  DualPressureArray,
  Turn,
  Quaternion,
} from '../../types/ski-data';
import { ColorSchemeId, ViewPreset } from '../../types/enums';
import RunOverview from '../replay/RunOverview';
import TurnList from '../replay/TurnList';
import DiagnosticPanel from '../replay/DiagnosticPanel';
import IndicatorDetail from '../replay/IndicatorDetail';
import Timeline from '../replay/Timeline';
import PressureHeatmap from '../visualization/PressureHeatmap';
import Pressure3DField from '../visualization/Pressure3DField';
import EdgingCurve from '../visualization/EdgingCurve';
import Insole3D from '../visualization/Insole3D';
import ColorScheme from '../visualization/ColorScheme';
import { usePlaybackMachine } from '../../state-machines/usePlaybackMachine';
import { findFrameIndex } from '../../hooks/useFrameData';
import { useElementSize } from '../../hooks/useElementSize';
import { useGuidedDemo } from '../narrative/useGuidedDemo';
import ChartAnnotation from '../narrative/ChartAnnotation';

/**
 * R2ReplayMain - Main three-column replay area (Region 2).
 *
 * Layout (fills viewport below the 56px header):
 *   ┌──────────┬─────────────────────┬──────────┐
 *   │ RunOverv │  three-channel viz  │ Indicator│
 *   │  (120px) │      (400px)        │  Detail  │
 *   ├──────────┤                     │ (200px)  │
 *   │ TurnList │─────────────────────│          │
 *   │ (scroll) │  DiagnosticPanel    │          │
 *   │          │      (200px)        │          │
 *   └──────────┴─────────────────────┴──────────┘
 *   │             Timeline (72px)                │
 *
 * Middle three-channel area (400px):
 *   - Left 50%: pressure visualization (2D heatmap / 3D height-field tab)
 *     with a color-scheme selector below the tabs.
 *   - Right 50%: top = edging curve (200px), bottom = 3D insole (200px).
 *
 * Data flow:
 *   - Receives the pipeline result (frames, pressureArrays, turns, runSession)
 *     as props from App.tsx.
 *   - Uses `usePlaybackMachine()` to read the current playback timestamp.
 *   - Looks up the current frame (binary search), pressure array (by index),
 *     and current turn (via the machine's currentTurnIndex) for visualization.
 */
interface R2ReplayMainProps {
  frames: SkiTickData[];
  pressureArrays: DualPressureArray[];
  turns: Turn[];
  runSession: RunSession;
}

/** Identity quaternion used before any frame data is available. */
const IDENTITY_QUATERNION: Quaternion = { w: 1, x: 0, y: 0, z: 0 };

/** Sampling interval at 100 Hz (ms), used to synthesize per-frame timestamps. */
const FRAME_INTERVAL_MS = 10;

export default function R2ReplayMain({
  frames,
  pressureArrays,
  turns,
  runSession,
}: R2ReplayMainProps) {
  // --- View state ---
  const [pressureView, setPressureView] = useState<'2d' | '3d'>('2d');
  const [colorScheme, setColorScheme] = useState<ColorSchemeId>(ColorSchemeId.Default);
  // Insole3D manages its own preset buttons internally; this state supplies the
  // initial/external preset value (Front / Side / Top / Free).
  const [viewPreset] = useState<ViewPreset>(ViewPreset.Free);

  // --- Current playback timestamp from SM-1 ---
  const playback = usePlaybackMachine();
  const playbackState = playback.state;
  const currentTimestamp = playbackState.currentTimestamp;

  // --- Current frame lookup (O(log n) binary search) ---
  const frameIndex = frames.length > 0 ? findFrameIndex(frames, currentTimestamp) : -1;
  const currentFrame: SkiTickData | null =
    frameIndex >= 0 && frameIndex < frames.length ? frames[frameIndex] : null;
  const currentPressure: DualPressureArray | null =
    frameIndex >= 0 && frameIndex < pressureArrays.length
      ? pressureArrays[frameIndex]
      : null;

  // --- Current turn (playback machine tracks a 1-based currentTurnIndex) ---
  const currentTurnIndex = playbackState.currentTurnIndex;
  const currentTurn: Turn | null =
    currentTurnIndex != null
      ? turns.find((t) => t.turn_index === currentTurnIndex) ?? null
      : null;

  // --- Edging curve series (from the current turn, if any) ---
  // When between turns the series are empty and EdgingCurve shows "暂无立刃数据".
  // The Turn type stores edging_sync_score (0-100) but not the raw time offset,
  // so syncOffsetMs is passed as 0; the curve still renders both edge series and
  // the current-time marker.
  const leftEdgeSeries = currentTurn?.edge_angle_frames.left ?? [];
  const rightEdgeSeries = currentTurn?.edge_angle_frames.right ?? [];
  const edgingTimestamps = currentTurn
    ? currentTurn.edge_angle_frames.left.map(
        (_, i) => currentTurn.start_ms + i * FRAME_INTERVAL_MS
      )
    : [];

  // --- Canvas container sizes (measured via ResizeObserver) ---
  const { ref: pressureAreaRef, size: pressureSize } = useElementSize<HTMLDivElement>();
  const { ref: edgingAreaRef, size: edgingSize } = useElementSize<HTMLDivElement>();
  const { ref: insoleAreaRef, size: insoleSize } = useElementSize<HTMLDivElement>();

  const { duration_ms } = runSession;

  // --- Guided demo (Narrative Shell) ---
  // 引导演示：空闲 5 秒后自动 seek 到最低分弯并慢速播放。
  const guidedDemo = useGuidedDemo({
    playbackState: playbackState.state,
    turns,
    seek: playback.seek,
    play: playback.play,
    setSpeed: playback.setSpeed,
  });

  // --- COP annotation for the pressure heatmap ---
  // 计算 COP 标注内容（外板压力占比）。
  const copAnnotation = useMemo(() => {
    if (!currentPressure) return { text: '', color: 'bad' as const, visible: false };
    // Calculate outside pressure ratio (right foot / total).
    // 计算外板压力占比（右脚 / 总压力）。
    const leftTotal = currentPressure.left.points.reduce((sum, p) => sum + p.value, 0);
    const rightTotal = currentPressure.right.points.reduce((sum, p) => sum + p.value, 0);
    const total = leftTotal + rightTotal;
    if (total === 0) return { text: '', color: 'bad' as const, visible: false };
    const ratio = rightTotal / total;
    if (ratio < 0.4) {
      return { text: 'COP 在鞋垫中心，未转移', color: 'bad' as const, visible: true };
    }
    if (ratio > 0.6) {
      return { text: 'COP 转移到外板', color: 'good' as const, visible: true };
    }
    return { text: '', color: 'ice' as const, visible: false };
  }, [currentPressure]);

  // --- Edging angle annotation ---
  // 计算立刃角标注（取左右立刃角绝对值的较大者作为当前立刃角）。
  const edgingAnnotation = useMemo(() => {
    if (!currentFrame) return { text: '', color: 'bad' as const, visible: false };
    const angle = Math.max(
      Math.abs(currentFrame.edge_angle_left),
      Math.abs(currentFrame.edge_angle_right)
    );
    if (angle < 25) {
      return { text: `${angle.toFixed(0)} 度，差 ${(25 - angle).toFixed(0)} 度`, color: 'bad' as const, visible: true };
    }
    return { text: `${angle.toFixed(0)} 度，达标`, color: 'good' as const, visible: true };
  }, [currentFrame]);

  return (
    <section className="r2-section">
      {/* Product positioning: watch summary vs per-turn diagnosis */}
      <div style={{
        padding: 'var(--space-sm) var(--space-lg)',
        background: 'var(--bg-elevated)',
        borderBottom: '1px solid var(--border-subtle)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 'var(--space-md)',
        fontSize: 'var(--fs-sm)',
      }}>
        <span style={{ color: 'var(--text-tertiary)' }}>
          手表数据：12 公里、48 次转弯
        </span>
        <span style={{ color: 'var(--text-tertiary)', fontWeight: 'var(--fw-bold)' }}>vs</span>
        <span style={{ color: 'var(--color-ice)', fontWeight: 'var(--fw-medium)' }}>
          逐弯诊断：第 7 弯入弯太急，压力没转过来
        </span>
      </div>

      {/* Three-column row */}
      <div className="r2-row">
        {/* Left column: RunOverview + TurnList */}
        <div className="r2-col-left">
          <RunOverview runSession={runSession} />
          <TurnList turns={turns} />
        </div>

        {/* Middle column: viz (400px) + diagnostic panel (200px) */}
        <div className="r2-col-center">
          {/* Three-channel visualization (400px) */}
          <div
            style={{
              height: '400px',
              flexShrink: 0,
              display: 'flex',
              borderBottom: '1px solid var(--border-subtle)',
            }}
          >
            {/* Left 50%: pressure visualization with tab switch + color scheme */}
            <div
              style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                borderRight: '1px solid var(--border-subtle)',
                minWidth: 0,
              }}
            >
              {/* Tab switcher: 2D 热力图 / 3D 高度场 */}
              <div
                style={{
                  display: 'flex',
                  gap: '4px',
                  padding: 'var(--space-xs) var(--space-md)',
                  borderBottom: '1px solid var(--border-subtle)',
                }}
              >
                {(['2d', '3d'] as const).map((tab) => (
                  <button
                    key={tab}
                    className="btn-hover"
                    onClick={() => setPressureView(tab)}
                    style={{
                      padding: '2px 10px',
                      fontSize: 'var(--fs-xs)',
                      borderRadius: 'var(--radius-sm)',
                      background:
                        pressureView === tab
                          ? 'var(--color-ice)'
                          : 'transparent',
                      color:
                        pressureView === tab
                          ? 'var(--bg-base)'
                          : 'var(--text-tertiary)',
                      border: '1px solid var(--border-default)',
                      cursor: 'pointer',
                      fontWeight:
                        pressureView === tab
                          ? 'var(--fw-semibold)'
                          : 'var(--fw-regular)',
                    }}
                  >
                    {tab === '2d' ? '2D 热力图' : '3D 高度场'}
                  </button>
                ))}
              </div>

              {/* Color scheme selector */}
              <div
                style={{
                  padding: 'var(--space-xs) var(--space-md)',
                  borderBottom: '1px solid var(--border-subtle)',
                }}
              >
                <ColorScheme
                  currentScheme={colorScheme}
                  onSchemeChange={setColorScheme}
                />
              </div>

              {/* Pressure canvas area (2D heatmap or 3D height field) */}
              <div
                ref={pressureAreaRef}
                style={{ flex: 1, minHeight: 0, overflow: 'hidden', position: 'relative' }}
              >
                {pressureSize.width > 0 &&
                  pressureSize.height > 0 &&
                  (pressureView === '2d' ? (
                    <PressureHeatmap
                      pressureArray={currentPressure}
                      colorScheme={colorScheme}
                      width={pressureSize.width}
                      height={pressureSize.height}
                    />
                  ) : (
                    <Pressure3DField
                      pressureArray={currentPressure}
                      width={pressureSize.width}
                      height={pressureSize.height}
                      colorScheme={colorScheme}
                    />
                  ))}
                <ChartAnnotation
                  x={pressureSize.width / 2}
                  y={20}
                  text={copAnnotation.text}
                  color={copAnnotation.color}
                  visible={copAnnotation.visible}
                />
              </div>
            </div>

            {/* Right 50%: edging curve (200px) + 3D insole (200px) */}
            <div
              style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                minWidth: 0,
              }}
            >
              {/* Edging curve (200px) */}
              <div
                ref={edgingAreaRef}
                style={{
                  height: '200px',
                  flexShrink: 0,
                  borderBottom: '1px solid var(--border-subtle)',
                  overflow: 'hidden',
                  position: 'relative',
                }}
              >
                {edgingSize.width > 0 &&
                  edgingSize.height > 0 && (
                    <EdgingCurve
                      leftEdgeSeries={leftEdgeSeries}
                      rightEdgeSeries={rightEdgeSeries}
                      timestamps={edgingTimestamps}
                      currentTimeMs={currentTimestamp}
                      syncOffsetMs={0}
                      width={edgingSize.width}
                      height={edgingSize.height}
                    />
                  )}
                <ChartAnnotation
                  x={edgingSize.width - 70}
                  y={20}
                  text={edgingAnnotation.text}
                  color={edgingAnnotation.color}
                  visible={edgingAnnotation.visible}
                />
              </div>

              {/* 3D insole posture (200px) */}
              <div
                ref={insoleAreaRef}
                style={{ height: '200px', flexShrink: 0, overflow: 'hidden' }}
              >
                {insoleSize.width > 0 &&
                  insoleSize.height > 0 && (
                    <Insole3D
                      quaternion={currentFrame?.quaternion ?? IDENTITY_QUATERNION}
                      width={insoleSize.width}
                      height={insoleSize.height}
                      viewPreset={viewPreset}
                    />
                  )}
              </div>
            </div>
          </div>

          {/* Diagnostic panel (200px) */}
          <DiagnosticPanel turns={turns} />
        </div>

        {/* Right column: IndicatorDetail */}
        <div className="r2-col-right">
          <IndicatorDetail runSession={runSession} />
        </div>
      </div>

      {/* Timeline (72px, full width) */}
      <Timeline turns={turns} durationMs={duration_ms} />

      {guidedDemo.state === 'demoing' && (
        <div style={{
          position: 'fixed',
          bottom: 'var(--space-lg)',
          right: 'var(--space-lg)',
          zIndex: 1000,
          padding: 'var(--space-xs) var(--space-md)',
          background: 'rgba(34, 211, 238, 0.15)',
          color: 'var(--color-ice)',
          fontSize: 'var(--fs-xs)',
          fontWeight: 'var(--fw-medium)',
          borderRadius: 'var(--radius-full)',
          border: '1px solid rgba(34, 211, 238, 0.3)',
        }}>
          演示中
        </div>
      )}
    </section>
  );
}
